/**
 * Шум Sentry в админке, который не является багом в нашем коде.
 * Паттерны выверены по реальным issue'ам (2023–2026).
 */
export const SENTRY_IGNORED_ERROR_PATTERNS: Array<RegExp> = [
  // WEB-1 (7651 событий, 152 пользователя, с 2023 года).
  // Axios кидает Cancel/Abort как отклонённый промис с undefined —
  // Sentry ловит их как "Non-Error promise rejection captured with value: undefined".
  // Это не баг, а нормальный сценарий отмены запроса при навигации.
  /Non-Error promise rejection captured with value: undefined/,

  // WEB-2Z / WEB-2Y: баг LightGallery — при быстром переключении slide=undefined.
  // Исправить можно только апгрейдом библиотеки, не наш код.
  /Cannot read properties of undefined \(reading '__slideVideoInfo'\)/,

  // Сетевые отмены на клиенте: пользователь закрыл вкладку / потерял сеть.
  // Для реальных серверных ошибок у нас есть 5xx-логи.
  /^Failed to fetch$/,
  /^Load failed$/,
  /NetworkError when attempting to fetch resource/,
  /The user aborted a request/,
  /^AbortError/,

  // WEB-8E — `AxiosError: Request aborted` (ECONNABORTED) на / (pageload).
  // Axios в xhr-адаптере кидает эту ошибку, когда XHR `onabort` срабатывает:
  // пользователь ушёл со страницы / закрыл вкладку до получения ответа.
  // Это нормальный SPA-сценарий: 20 событий / 13 пользователей с 2026-01,
  // все на первом рендере админки. UI уже переживает это через
  // react-router — второго ивента без стека нашего кода в Sentry не нужно.
  //
  // Префикс `AxiosError: ` опционален — ровно по той же причине, что и у
  // WEB-31 ниже. Axios в конструкторе делает `this.message = message` и
  // `this.name = 'AxiosError'` РАЗДЕЛЬНО, а Sentry в `exceptionFromError`
  // кладёт `error.name` в `exception.values[0].type`, а `error.message` —
  // в `.value`. `sentryBeforeSend` читает только `value` (и `original.message`,
  // который тоже голый), то есть на вход придёт `Request aborted` без
  // префикса. Склеенную форму `AxiosError: Request aborted` показывает
  // только UI Sentry (заголовок issue = `{type}: {value}`) и `toString()`.
  // Покрываем обе, чтобы фильтр не зависел от того, откуда взялась строка.
  //
  // Якорь `^...$` — чтобы не задеть осмысленные `Request aborted: <detail>`
  // (сейчас таких форм не наблюдаем, но перестраховываемся).
  /^(?:AxiosError:\s)?Request aborted$/,
  // WEB-4Q / WEB-5S: axios-таймаут с локализованным сообщением.
  // Суть — сетевая проблема на стороне клиента, не баг приложения.
  /Превышен таймаут/,
  /timeout of \d+ms exceeded/,

  // WEB-31 (~298 событий / 8 пользователей в сутки). Axios в `xhr`-адаптере
  // делает `reject(new AxiosError('Network Error', AxiosError.ERR_NETWORK, ...))`,
  // когда XHR отдаёт `onerror` без дополнительного контекста — почти всегда
  // это означает «у пользователя оборвалась сеть до получения заголовков
  // ответа» (закрыл вкладку, переключил Wi-Fi, упал DPI). UI уже
  // обрабатывает это через axios-interceptor и показывает экран ошибки;
  // в Sentry эти события не несут actionable инфы и группируются в одну
  // огромную issue без полезного стека.
  //
  // Покрываем обе формы: bare `Network Error` (если Sentry хранит type
  // отдельно и beforeSend читает только `value`) и склеенное
  // `AxiosError: Network Error`. Якорь `^...$` — чтобы не задеть
  // произвольные сообщения с подстрокой "Network Error" из других контекстов.
  /^(?:AxiosError:\s)?Network Error$/,

  // WEB-1R (969 событий, 10 пользователей, с 2023).
  // Axios при 5xx от upstream (чаще всего nginx 502 Bad Gateway от
  // проксирующего слоя перед backend) отдаёт в промис отклонение с
  // HTML-телом ошибки. Sentry эмитит это как:
  //   "UnhandledRejection: Non-Error promise rejection captured with value: <html>...502 Bad Gateway..."
  // На сервере мы уже видим исходную 5xx-причину — дубль HTML с админки не нужен.
  //
  // Матчим характерную пару: стандартное "Non-Error promise rejection" от
  // Sentry + HTML (опционально с `<!DOCTYPE html>`) + nginx/Cloudflare
  // статус-фраза с настоящим 5xx-кодом. Произвольные сообщения с подстрокой
  // "<html>" или 4xx-страницы сюда не попадают.
  //
  // `Service (?:Temporarily )?Unavailable` покрывает оба варианта nginx 503:
  // как стандартный "Service Unavailable", так и "Service Temporarily
  // Unavailable" (так nginx отдаёт по умолчанию из upstream-проксирования).
  /Non-Error promise rejection captured with value:\s*(?:<!DOCTYPE html>\s*)?<html>[\s\S]*?\b5\d{2}\s+(?:Bad Gateway|Service (?:Temporarily )?Unavailable|Gateway Timeout|Internal Server Error)\b/i,
  // WEB-5G (334 события / 2 пользователя): тот же класс шума, но без префикса
  // "Non-Error promise rejection". Когда axios получает 5xx HTML от nginx,
  // некоторые call-site'ы (`PurchaseOrderReceiptConfirmationModal: api`) не
  // отдают reject в виде Promise<undefined>, а сами оборачивают тело в
  // `new Error(htmlBody)` и кидают через captureException — Sentry в этом
  // случае кладёт всю HTML-страницу в `event.message` без обёртки. Поэтому
  // одного паттерна выше недостаточно.
  //
  // Якорь "^\s*(?:<!DOCTYPE\s+html[^>]*>\s*)?<html(?:\s[^>]*)?>" покрывает:
  //   <html>...</html>, <html lang="en">...</html>,
  //   <!DOCTYPE html><html>...</html>, лидирующие пробелы / переводы строки.
  /^\s*(?:<!DOCTYPE\s+html[^>]*>\s*)?<html(?:\s[^>]*)?>[\s\S]*?\b(?:5\d{2})\s+(?:Bad Gateway|Service (?:Temporarily )?Unavailable|Gateway Timeout|Internal Server Error)\b/i,
  // WEB-29 (660 событий, 10 пользователей, с 2024).
  // Форма события (Sentry title): "UnhandledRejection: Object captured as
  // promise rejection with keys: error", дополнительные данные содержат
  // `{ error: "Transaction API error: Transaction already closed: ..." }`.
  // Это серверная Prisma-ошибка, которую axios пробрасывает в promise
  // rejection, а Sentry сериализует объект. Сам баг уже виден у backend.
  //
  // Поддерживаем и чистую строку (как описано в Sentry SDK), и вариант с
  // префиксом "UnhandledRejection: " который показывает UI Sentry.
  /^(?:UnhandledRejection:\s+)?Object captured as promise rejection with keys: error$/,

  // WEB-32 (990 событий / 2 пользователя): NotFoundError из react-dom@18 в проде.
  // Классический симптом вмешательства Google Translate / похожего расширения
  // в commit-фазу React: расширение перетасовывает DOM-узлы, и React
  // не может найти ребёнка для удаления. Не наш код.
  // Сознательно НЕ фильтруем общие "Cannot read properties of null (reading 'parentNode')"
  // — слишком широко, может скрыть реальные null-deref баги. Если parentNode-вариант
  // того же семейства появится в Sentry — добавим отдельно с проверкой stack-frame.
  /Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node/,
];

/**
 * Подстроки в путях Sentry-фрейма, которые означают, что ошибка прилетела
 * из стороннего пакета и не является нашим багом. Используется как
 * дополнительный гейт по стектрейсу (когда message слишком общее, чтобы
 * безопасно матчить по нему). Проверяем все три места, куда Sentry может
 * положить путь к источнику: filename, abs_path, module.
 */
export const SENTRY_IGNORED_FRAME_SUBSTRINGS: ReadonlyArray<string> = [
  // WEB-37 (415 событий, 3 пользователя, с 2024-06).
  // `TypeError: Cannot read properties of undefined (reading 'height')` в
  // `Zoom.prototype.getDragAllowedAxises` из lightgallery@2.7.2 — известный
  // баг zoom-плагина: при mousedown на ещё не инициализированный слайд
  // `this.containerRect` остаётся undefined. Поправить можно только апгрейдом
  // либы (как и WEB-2Y/2Z). Сообщение слишком общее, поэтому фильтруем по
  // фрейму, а не по тексту, чтобы не глушить настоящие ошибки `.height` в
  // нашем коде.
  'lightgallery/plugins/zoom/lg-zoom',
];

export function isIgnoredSentryMessage(message: unknown): boolean {
  if (typeof message !== 'string' || !message) return false;
  return SENTRY_IGNORED_ERROR_PATTERNS.some((p) => p.test(message));
}

type SentryFrame = {
  filename?: string;
  abs_path?: string;
  module?: string;
};
type SentryEvent = {
  message?: string;
  exception?: {
    values?: Array<{
      value?: string;
      stacktrace?: { frames?: SentryFrame[] };
    }>;
  };
};

function frameMatches(frame: SentryFrame): boolean {
  // Бандлеры/source-maps по-разному кладут путь к источнику. Безопаснее
  // проверять все три поля и не зависеть от того, что включено для конкретного
  // ивента. (Например, минифицированный браузерный фрейм может иметь только
  // abs_path = "https://admin/static/js/main.abc.js" без filename, а
  // source-mapped — наоборот.)
  for (const candidate of [frame.filename, frame.abs_path, frame.module]) {
    if (typeof candidate !== 'string') continue;
    for (const sub of SENTRY_IGNORED_FRAME_SUBSTRINGS) {
      if (candidate.includes(sub)) return true;
    }
  }
  return false;
}

function hasIgnoredFrame(event: SentryEvent): boolean {
  const frames = event.exception?.values?.flatMap(
    (v) => v.stacktrace?.frames ?? [],
  );
  if (!frames || frames.length === 0) return false;
  return frames.some(frameMatches);
}

export function sentryBeforeSend<E extends SentryEvent>(
  event: E,
  hint?: { originalException?: unknown },
): E | null {
  const original = hint?.originalException;
  const originalMessage =
    original instanceof Error ? original.message : undefined;
  const eventMessage =
    event.message ?? event.exception?.values?.[0]?.value ?? undefined;

  if (
    isIgnoredSentryMessage(originalMessage) ||
    isIgnoredSentryMessage(eventMessage) ||
    hasIgnoredFrame(event)
  ) {
    return null;
  }
  return event;
}
